import { z } from "zod";

import { validatePaymentPlanShape } from "./PaymentShapeValidation";

const PAYMENT_TRANSACTION_CODE_MAX = 200;
const CASE_PAYMENT_INSTALLMENT_LIMIT = 999;

const hasAtMostTwoDecimalPlaces = (value: number): boolean =>
  Math.abs(value - Math.round(value * 100) / 100) < 1e-9;

const PaymentAmountSchema = z
  .number({ message: "case_payment_validation_amount" })
  .nonnegative({ message: "case_payment_validation_amount" })
  .refine(hasAtMostTwoDecimalPlaces, { message: "case_payment_validation_amount_precision" });

const PositivePaymentAmountSchema = PaymentAmountSchema.positive({
  message: "case_payment_validation_total_positive",
});

const InstallmentCountSchema = z
  .number({ message: "case_payment_validation_installment_count" })
  .int({ message: "case_payment_validation_installment_count" })
  .min(0, { message: "case_payment_validation_installment_count" })
  .max(CASE_PAYMENT_INSTALLMENT_LIMIT, {
    message: "case_payment_validation_installment_count",
  });

const ConfirmationFieldsSchema = z.object({
  transactionCode: z
    .string()
    .trim()
    .max(PAYMENT_TRANSACTION_CODE_MAX, {
      message: "case_payment_validation_transaction_code_length",
    })
    .optional(),
  paymentDate: z.iso.date({ message: "case_payment_validation_payment_date_required" }),
});

const CasePaymentMode = z.enum(["upfront", "interest_free", "interest_bearing"]);
export type CasePaymentMode = z.infer<typeof CasePaymentMode>;

const CasePaymentStatus = z.enum(["paid", "overdue", "upcoming"]);
export type CasePaymentStatus = z.infer<typeof CasePaymentStatus>;

const CasePaymentConfirmationInputSchema = z.discriminatedUnion("kind", [
  ConfirmationFieldsSchema.extend({ kind: z.literal("upfront") }),
  ConfirmationFieldsSchema.extend({
    kind: z.literal("installment"),
    installmentNumber: z.number().int().min(1).max(CASE_PAYMENT_INSTALLMENT_LIMIT),
  }),
]);
export type CasePaymentConfirmationInput = z.infer<typeof CasePaymentConfirmationInputSchema>;

const CasePaymentConfirmationSchema = z.discriminatedUnion("kind", [
  ConfirmationFieldsSchema.extend({
    kind: z.literal("upfront"),
    amount: PositivePaymentAmountSchema,
    scheduledDueDate: z.iso.date(),
  }),
  ConfirmationFieldsSchema.extend({
    kind: z.literal("installment"),
    installmentNumber: z.number().int().min(1).max(CASE_PAYMENT_INSTALLMENT_LIMIT),
    amount: PositivePaymentAmountSchema,
    scheduledDueDate: z.iso.date(),
  }),
]);
export type CasePaymentConfirmation = z.infer<typeof CasePaymentConfirmationSchema>;

const CasePaymentConditionsSchema = z.object({
  mode: CasePaymentMode,
  totalAmount: PositivePaymentAmountSchema,
  upfrontAmount: PaymentAmountSchema,
  upfrontDate: z.iso.date().optional(),
  installmentCount: InstallmentCountSchema,
  firstInstallmentDueDate: z.iso.date().optional(),
});

export const CasePaymentPlanInputSchema = CasePaymentConditionsSchema.extend({
  confirmations: z.array(CasePaymentConfirmationInputSchema),
}).superRefine(validatePaymentPlanShape);
export type CasePaymentPlanInput = z.infer<typeof CasePaymentPlanInputSchema>;

export const CasePaymentPlanSchema = CasePaymentConditionsSchema.extend({
  confirmations: z.array(CasePaymentConfirmationSchema),
}).superRefine(validatePaymentPlanShape);
export type CasePaymentPlan = z.infer<typeof CasePaymentPlanSchema>;

export type CasePaymentRow = {
  key: string;
  kind: "upfront" | "installment";
  installmentNumber?: number;
  amount: number;
  scheduledDueDate: string;
  transactionCode?: string;
  paymentDate?: string;
  status: CasePaymentStatus;
};

export type CasePaymentSummary = {
  totalReceived: number;
  overdueReceivable: number;
  upcomingReceivable: number;
};

export type CasePaymentNormalizationError =
  | "case_payment_validation_policy"
  | "case_payment_validation_installment_cap"
  | "case_payment_validation_confirmed_upfront_locked"
  | "case_payment_validation_confirmed_installment_count"
  | "case_payment_validation_confirmed_total"
  | "case_payment_validation_confirmation_reference";

export type CasePaymentNormalizationResult =
  | { success: true; data: CasePaymentPlan }
  | { success: false; error: CasePaymentNormalizationError };
